import { loadIndexes } from '@data/indexes';
import DOMPurify from 'dompurify';
import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import { describeArmesMaitrisees, describeArmuresMaitrisees } from '../domain/armement';

export async function renderProfilDetailPage(id: string): Promise<unknown> {
  const idx = await loadIndexes();
  const profil = idx.profilById.get(id);
  if (!profil) {
    return html`<h1 class="page-title">Profil introuvable</h1>
      <p>L'identifiant "${id}" ne correspond a aucun profil.</p>`;
  }
  const voies = idx.voiesClasseByProfil.get(id) ?? [];
  const isMageProfile = idx.voieDuMage?.profils_ids?.includes(id) ?? false;
  const voieDuMage = isMageProfile ? idx.voieDuMage : null;

  const narratifHtml = profil.narratif
    ? DOMPurify.sanitize(await marked.parse(profil.narratif))
    : null;

  const armes = describeArmesMaitrisees(profil.armement.capacites);
  const armures = describeArmuresMaitrisees(profil.armement.capacites);

  return html`
    <article class="profil-detail">
      <!-- Header -->
      <header class="profil-header">
        <div class="profil-header-top">
          <span class="profil-famille-badge">${profil.famille_id}</span>
        </div>
        <h1 class="profil-name" data-morph data-morph-duration="1200">${profil.nom}</h1>
        <div class="profil-meta">
          <span class="profil-meta-item">PV/niv ${profil.pv_par_niveau}</span>
          <span class="profil-meta-sep"></span>
          <span class="profil-meta-item">${profil.caracteristiques_prioritaires.join(', ')}</span>
        </div>
      </header>

      <!-- Narratif -->
      ${
        narratifHtml
          ? html`
        <section class="profil-section profil-narratif">
          <div class="profil-prose">${unsafeHTML(narratifHtml)}</div>
        </section>
      `
          : null
      }

      <!-- Equipement -->
      <section class="profil-section">
        <h2 class="profil-section-title">Equipement de depart</h2>
        <ul class="profil-equip-list">
          ${profil.equipement_depart.map(
            (e) => html`
            <li class="profil-equip-item">
              <span class="profil-equip-name">${e.item}</span>
              ${e.stats ? html`<span class="profil-equip-stats">${e.stats}</span>` : null}
              ${e.optionnel ? html`<span class="profil-equip-opt">optionnel</span>` : null}
            </li>
          `,
          )}
        </ul>
      </section>

      <!-- Restrictions -->
      <section class="profil-section">
        <h2 class="profil-section-title">Maitrises</h2>
        <div class="profil-restrictions">
          <div class="profil-restriction-group">
            <span class="profil-restriction-label">Armes</span>
            <span class="profil-restriction-value">${armes.join(', ')}</span>
          </div>
          <div class="profil-restriction-group">
            <span class="profil-restriction-label">Armures</span>
            <span class="profil-restriction-value">${armures.join(', ')}</span>
          </div>
        </div>
      </section>

      <!-- Voies -->
      <section class="profil-section">
        <h2 class="profil-section-title">Voies <span class="profil-voie-count">${voies.length}</span></h2>
        <div class="profil-voies">
          ${voies.map(
            (v, i) => html`
            <div class="profil-voie-block" style="animation-delay: ${i * 50}ms">
              <cof2-accordion label=${v.nom}>
                <div class="profil-rangs">
                  ${v.rangs.map(
                    (r) => html`
                    <div class="profil-rang">
                      <div class="profil-rang-header">
                        <span class="profil-rang-num">${r.rang}</span>
                        <span class="profil-rang-name">${r.nom}</span>
                        ${r.type ? html`<span class="profil-rang-type">${r.type}</span>` : null}
                      </div>
                      <p class="profil-rang-desc">${r.description}</p>
                    </div>
                  `,
                  )}
                </div>
              </cof2-accordion>
            </div>
          `,
          )}
        </div>
      </section>

      <!-- Voie du Mage -->
      ${
        voieDuMage
          ? html`
        <section class="profil-section">
          <h2 class="profil-section-title">Voie du Mage <span class="profil-voie-shared">partagee</span></h2>
          <p class="profil-mage-note">
            Accessible aux 4 profils mage. Peut remplacer la voie de peuple au niveau 1.
          </p>
          <div class="profil-voie-block">
            <cof2-accordion label=${voieDuMage.nom}>
              <div class="profil-rangs">
                ${voieDuMage.rangs.map(
                  (r) => html`
                  <div class="profil-rang">
                    <div class="profil-rang-header">
                      <span class="profil-rang-num">${r.rang}</span>
                      <span class="profil-rang-name">${r.nom}</span>
                      ${r.type ? html`<span class="profil-rang-type">${r.type}</span>` : null}
                    </div>
                    <p class="profil-rang-desc">${r.description}</p>
                  </div>
                `,
                )}
              </div>
            </cof2-accordion>
          </div>
        </section>
      `
          : null
      }
    </article>
  `;
}
